/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelDataJson } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { IBusinessUnit } from '@/interfaces/business-unit';
import {
  IProductAllocation,
  IProductAllocationReq,
  IUpdateProductAllocation,
} from '@/interfaces/product';
import { formatNumber } from '@/utils/dataUtils';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { updateProductAllocations } from '@/adapters/product';
import { twMerge } from 'tailwind-merge';
import { IMediaUpload } from '@/interfaces/media';
import { uploadMedia } from '@/adapters/media';

interface IProps {
  bus: IBusinessUnit[];
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: IProductAllocationReq = {
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  name: '',
  search: '',
};

const ImportAllocationModal = ({
  bus,
  open,
  handleOk,
  handleCancel,
}: IProps) => {
  const [criteria, setCriteria] = useState<IProductAllocationReq>({
    ..._criteria,
  });
  const [products, setProducts] = useState<IProductAllocation[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<IProductAllocation>
      | SorterResult<IProductAllocation>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IProductAllocation) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<IProductAllocation>['columns'] = [
    {
      title: 'รหัส',
      dataIndex: 'ProductCode',
      key: 'ProductCode',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'ProductName',
      key: 'ProductName',
      className: 'font-bold',
    },
    {
      title: 'สถานะ',
      dataIndex: 'Status',
      key: 'Status',
      width: 100,
      align: 'center',
      render: (_: boolean) => {
        return _ ? (
          <CheckCircleFilled className="text-green-600" />
        ) : (
          <CloseCircleFilled className="text-red-600" />
        );
      },
    },
  ];

  bus?.map(c => {
    columns.push({
      title: `${c.name}`,
      dataIndex: `${c.name}`,
      key: `${c.name}`,
      className: 'font-bold',
      width: 130,
      align: 'center',
      render: _ => <div>{(_ && formatNumber(Number(_ || 0))) || ''}</div>,
    });
  });

  columns.push({
    title: 'สถานะ',
    dataIndex: 'validate',
    key: 'validate',
    fixed: 'right',
    align: 'center',
    sorter: (a, b) => Number(a.validate) - Number(b.validate),
    defaultSortOrder: 'ascend',
    width: 80,
    className: 'font-bold',
    render: _ => (
      <Space
        className={twMerge(
          _ === 2 && 'text-red-600',
          _ === 3 && 'text-green-700',
        )}
      >
        {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
      </Space>
    ),
  });

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setProducts([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelDataJson(file)) as any[];
      excelData = excelData.map((c: any) => {
        let percent = 0;
        for (const key in c) {
          if (
            key !== 'ProductCode' &&
            key !== 'ProductName' &&
            key !== 'Status' &&
            key !== 'IsActive'
          ) {
            percent += Number(c[key as keyof IProductAllocation]) || 0;
          }
        }
        c.Status = c.Status == '1';
        if (percent != 100) {
          c.validate = 2;
        } else {
          c.validate = 3;
        }
        return c;
      });
      console.log(excelData);
      setProducts(excelData);
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const upLoadmutations = useMutation({
    mutationFn: async (data: Partial<IMediaUpload>) => await uploadMedia(data),
    mutationKey: ['upload-media'],
    onSuccess: data => {
      console.log(data);
    },
    onError: error => {
      console.log(error);
    },
  });

  const mutations = useMutation({
    mutationFn: async (data: Partial<IUpdateProductAllocation>[]) =>
      await updateProductAllocations(data),
    mutationKey: ['import-product-allocation'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setProducts([]);
        notification.success({
          message: 'นำเข้า Product Allocation สำเร็จ',
        });
        const file: Partial<IMediaUpload> = {
          file: fileList.length ? (fileList[0] as any) : null,
          modelId: 1,
          modelType: 'product-allocation',
        };
        upLoadmutations.mutateAsync(file);
        handleOk();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    const dataImport = products.flatMap(product => {
      const productCode = product.ProductCode;
      const isActive = product.Status;
      const result: Partial<IUpdateProductAllocation>[] = [];
      for (const key in product) {
        if (
          key !== 'ProductCode' &&
          key !== 'ProductName' &&
          key !== 'Status' &&
          key !== 'validate' &&
          key !== 'TotalStock' &&
          key !== 'IsActive'
        ) {
          result.push({
            productCode,
            isActive: isActive,
            bu: key, // Key represents the "Bu"
            percent: Number(product[key as keyof IProductAllocation]) || 0,
          });
        }
      }
      return result;
    });
    mutations.mutateAsync(dataImport);
  };

  const isDisabled = products?.some(c => c.validate == 2);

  return (
    <Modal
      title="นำเข้า Product Allocation"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          disabled={isDisabled}
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
      afterClose={() => {
        setFileList([]);
        setProducts([]);
      }}
    >
      <div className="flex flex-col gap-2 w-full">
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Upload {...props}>
              {fileList.length === 0 && (
                <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
              )}
            </Upload>
          </Col>
        </Row>
        <TableComponent<IProductAllocation>
          columns={columns}
          criteria={criteria}
          data={{
            data: products,
            totalRecords: products.length,
          }}
          loading={false}
          onTableChange={onTableChange}
          scroll
          rowKey="ProductCode"
        />
      </div>
    </Modal>
  );
};

export default ImportAllocationModal;
